import { getMessage } from '@tcc/i18n';
import {
  FIELD_SUMMARY,
  PLANTING_CATEGORY,
  PLANTING_IDS,
  HARVEST_CATEGORY,
  HARVEST_IDS,
  IN_SEASON_CATEGORY,
  IN_SEASON_IDS,
  BASE_LAYERS_CATEGORY,
  BASE_LAYER_IDS,
  SOILSURGO_LAYERS,
  IN_SEASON,
} from '../constants/mapLayerIds';
import { filterSoilSurgoLayers } from '../utils/getsoilsSurgoLayer';
import { FieldFeature } from '../types';
import { LayersBySeason, MapLayer } from '../types/Layer';

type FilterableLayerOption = {
  id: string;
  name: string;
  disabled?: boolean;
};

type LayerOption = {
  id: string;
  name: string;
  disabled: boolean;
};

type LayerCategoryOption = {
  id: string;
  name: string;
  disabled?: boolean;
  subOptions?: FilterableLayerOption[];
};

/**
 * Return an array without the suboption to hide.
 * @param {Array} subOptions
 * @param {String} subOptionToHideId
 * @return {Array}
 */
export const hideSubOption = (
  subOptions: FilterableLayerOption[],
  subOptionToHideId: string
): FilterableLayerOption[] =>
  subOptions.filter((obj) => obj.id !== subOptionToHideId);

const hasAgVizData = (layer: MapLayer) => 
  typeof layer === 'object' && layer !== null && 'agVizTileUrl' in layer && layer.agVizTileUrl

interface GetLayerIdParams {
  layers: LayersBySeason;
  seasonId: string;
  id: string;
  agVizEnabled: boolean;
}

const getLayer = ({ layers, seasonId, id, agVizEnabled }: GetLayerIdParams): LayerOption => {
  const layer = layers[seasonId]?.[id] || layers.seasonAgnostic?.[id]

  const nameId =
    typeof layer === 'object' && layer !== null && 'nameId' in layer
      ? (layer as { nameId?: string }).nameId
      : undefined;

  return {
    id,
    name: getMessage(`layer.${nameId || id}`),
    disabled: agVizEnabled ? !hasAgVizData(layer) : !layer,
  };
}

/**
 * Returns an array of subOption map layers for the provided ids. For each subOption id,
 * if it is not present in the layers object, return that layer as disabled.
 * @param {Object} layers - Object of all map layers loaded in the app
 * @param {Array} ids - Array of map layer IDs to be returned
 * @param {String} seasonId
 * @param {Array} [disabledLayers=[]] the layers that are not to appear
 * @return {Array} - Array of map layer objects
 */
export const getSubOptionLayers = (
  layers: LayersBySeason,
  ids: string[],
  seasonId: string,
  disabledLayers: string[] = [],
  agVizEnabled = false
): LayerOption[] => {

  const filteredIds = ids.filter(
    (id) => ![...SOILSURGO_LAYERS, ...disabledLayers].includes(id)
  );

  return filteredIds.map((id) => getLayer({layers, seasonId, id, agVizEnabled}));
};


/**
 * Check and remove those premium features that the user doesnt have
 * the needed asset on their subscription tier.
 * @param {Array} subOptions
 * @param {Array} fieldAssets
 * @return {Array}
 */
export const checkInSeasonSubOptionsAssets = (
  subOptions: FilterableLayerOption[],
  fieldAssets: string[],
  hideSpatialDisplayFeatureLDFlag = false
): FilterableLayerOption[] => {
  let newSubOptions = [...subOptions];
  if (hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY)) {
    newSubOptions = hideSubOption(newSubOptions, IN_SEASON.APPLICATIONS);
  }
  if (!fieldAssets.includes(FieldFeature.FIELD_HEALTH_ADVISOR)) {
    newSubOptions = hideSubOption(newSubOptions, IN_SEASON.FHA);
  }
  return newSubOptions;
}

/**
 * Returns an array of all map layer objects. Some map layer objects returned are simple
 * (for example, Field Summary). Most are held within sublists (for example, Planting).
 * Each sublist has name and disabled values. Each map layer object has both of those
 * values along with an id value, which is used in the URL hash and with the Selector
 * Crate component.
 * @param {String} seasonId
 * @param {Object} layers - List of all map layers
 * @param {Array} [disabledLayers=[]] the layers that are not to appear
 * @return {Array}
 */
export const getMapLayers = (
  seasonId: string,
  layers: LayersBySeason,
  disabledLayers: string[] = [],
  fieldAssets: string[] = [],
  hideSpatialDisplayFeatureLDFlag = false,
  hasHarvestDataForActiveSeason = true,
  agVizEnabled = false
): LayerCategoryOption[] => {

  const plantingSubOptions = getSubOptionLayers(
    layers,
    PLANTING_IDS,
    seasonId,
    disabledLayers,
    agVizEnabled,
  );
  
  const harvestSubOptions = getSubOptionLayers(
    layers,
    HARVEST_IDS,
    seasonId,
    disabledLayers,
    agVizEnabled,
  );

  let baseLayerSubOptions = getSubOptionLayers(
    layers,
    BASE_LAYER_IDS,
    seasonId,
    disabledLayers,
  );
  const inSeasonSubOptions = getSubOptionLayers(
    layers,
    IN_SEASON_IDS,
    seasonId,
    disabledLayers
  );

  baseLayerSubOptions = filterSoilSurgoLayers(
    baseLayerSubOptions,
    layers.seasonAgnostic?.soilssurgo
  ) as LayerOption[];

  const plantingEnabled = plantingSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const harvestEnabled = hasHarvestDataForActiveSeason && harvestSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const baseLayersEnabled = baseLayerSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const inSeasonEnabled = inSeasonSubOptions.some(
    (subOption) => !subOption.disabled
  );

  return [
    {
      name: getMessage(`layer.${FIELD_SUMMARY}`),
      id: FIELD_SUMMARY,
    },
    ...(hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY) ? [] : [{
      name: getMessage('layer.planting'),
      id: PLANTING_CATEGORY,
      disabled: !plantingEnabled,
      subOptions: plantingSubOptions,
    }]),
    {
      name: getMessage('layer.inSeason'),
      id: IN_SEASON_CATEGORY,
      disabled: !inSeasonEnabled,
      subOptions: checkInSeasonSubOptionsAssets(inSeasonSubOptions, fieldAssets, hideSpatialDisplayFeatureLDFlag)
    },
    ...(hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY) ? [] : [{
      name: getMessage('layer.harvest'),
      id: HARVEST_CATEGORY,
      disabled: !harvestEnabled,
      subOptions: harvestSubOptions,
    }]),
    {
      name: getMessage('layer.baseLayers'),
      id: BASE_LAYERS_CATEGORY,
      disabled: !baseLayersEnabled,
      subOptions: baseLayerSubOptions,
    },
  ];
};